import type { AxiosError, InternalAxiosRequestConfig } from 'axios'
import axios from 'axios'
import { showDialog, Toast } from 'vant'
// import { locale } from '@/utils/i18n'
import { useUserStore } from '@/stores/modules/user'
import useRouteCacheStore from '@/stores/modules/routeCache'
import { useRouter } from 'vue-router'

import { useI18n } from 'vue-i18n'
const router = useRouter()

/**
 * 获取多语言图片路径
 * @param imageKey 图片键名，例如 'sidebar.indiqueAmigos'
 * @returns 图片路径
 */
export const getMultilingualImage = (imageKey: string): string => {
  const { t } = useI18n()

  try {
    // 从语言包中获取图片路径
    const imagePath = t(`images.${imageKey}`)

    // 如果找到了图片路径，使用 getAssetsFile 获取完整路径
    if (imagePath && imagePath !== `images.${imageKey}`) {
      return getAssetsFile(imagePath)
    }

    // 如果语言包中没有配置，返回默认路径
    return getAssetsFile(imageKey)
  } catch (error) {
    console.warn(`Failed to get multilingual image for key: ${imageKey}`, error)
    return getAssetsFile(imageKey)
  }
}

/**
 * 获取侧边栏多语言图片
 * @param type 图片类型：'indiqueAmigos' | 'comissaoDe'
 * @returns 图片路径
 */
export const getSidebarImage = (type: 'indiqueAmigos' | 'comissaoDe'): string => {
  return getMultilingualImage(`sidebar.${type}`)
}

/**
 * 解析JSON格式的多语言图片数据
 * @param imageData JSON字符串或对象，格式如: {"en":"hash-admin/de7704ca-8dbe-45b8-aec9-56025bf6329b.jpg","id":"hash-admin/b44cdf88-428b-4a7c-a1e7-fdd08cc342e5.png"}
 * @returns 根据当前语言返回对应的图片路径
 */
export const parseMultilingualImageData = (imageData: string | object): string => {
  const { locale } = useI18n()

  try {
    // 如果是字符串，尝试解析为JSON
    const data = typeof imageData === 'string' ? JSON.parse(imageData) : imageData

    // 获取当前语言
    const currentLang = locale.value || 'pt'

    // 语言代码映射 - 同时支持多种可能的键名
    const langMapping: Record<string, string[]> = {
      'pt-BR': ['pt', 'pt-BR', 'pt-BR'],
      'en-US': ['en', 'en-US'],
      'hi-IN': ['id', 'hi-IN', 'hi'], // 印地语支持多种可能的键名
      'tl-PH': ['tl', 'tl-PH'], // 菲律宾语支持
      'en': ['en', 'en-US'],
      'id': ['id', 'hi-IN', 'hi'],
      'pt': ['pt', 'pt-BR', 'pt-BR'], // 添加葡萄牙语映射
      'tl': ['tl', 'tl-PH'] // 菲律宾语简化代码
    }

    // 获取映射后的语言代码数组
    const mappedLangs = langMapping[currentLang] || [currentLang]

    // 按优先级尝试不同的语言代码
    for (const lang of mappedLangs) {
      if (data[lang]) {
        return data[lang]
      }
    }

    // 如果当前语言不存在，返回英文版本作为默认
    if (data.en) {
      return data.en
    }

    // 如果英文版本也不存在，返回第一个可用的图片
    const firstLang = Object.keys(data)[0]
    if (firstLang) {
      return data[firstLang]
    }

    // 如果都没有，返回空字符串
    return ''
  } catch (error) {
    console.warn('解析多语言图片数据失败:', error)
    // 如果解析失败，直接返回原始数据（如果是字符串的话）
    return typeof imageData === 'string' ? imageData : ''
  }
}

// 这里是用于设定请求后端时，所用的 Token KEY
export const REQUEST_TOKEN_KEY = 'Authorization'

// 缓存配置
interface CacheItem {
  data: any
  timestamp: number
  ttl: number
}

// 请求缓存
const requestCache = new Map<string, CacheItem>()

// 进行中的请求去重
const pendingRequests = new Map<string, Promise<any>>()

// 轮播图缓存时间 10 分钟
const BANNER_CACHE_TTL = 10 * 60 * 1000

// 跑马灯缓存时间（比如10分钟）
const MARQUEE_CACHE_TTL = 10 * 60 * 1000

// 厂商列表缓存时间 15 分钟
const VENDORS_CACHE_TTL = 15 * 60 * 1000

// 创建请求唯一标识
function createRequestKey(config: InternalAxiosRequestConfig): string {
  const { method, url, params, data } = config
  return `${method}_${url}_${JSON.stringify(params || {})}_${JSON.stringify(data || {})}`
}

// 缓存管理
class CacheManager {
  static get(key: string): any | null {
    const item = requestCache.get(key)
    if (!item) return null

    if (Date.now() - item.timestamp > item.ttl) {
      requestCache.delete(key)
      return null
    }

    return item.data
  }

  static set(key: string, data: any, ttl: number): void {
    requestCache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    })
  }

  static clear(): void {
    requestCache.clear()
  }

  static clearExpired(): void {
    const now = Date.now()
    for (const [key, item] of requestCache.entries()) {
      if (now - item.timestamp > item.ttl) {
        requestCache.delete(key)
      }
    }
  }
}

// 创建 axios 实例
const request = axios.create({
  baseURL: import.meta.env.VITE_BASE_URL,
  timeout: 6000,
})

export type RequestError = AxiosError<{
  message?: string
  result?: any
  errorMessage?: string
}>

// 异常拦截处理器
function errorHandler(error: RequestError): Promise<any> {
  // debugger
  if (error.response) {
    const { data = {}, status, statusText } = error.response
    if (status === 403) {
      showDialog({
        title: 'Error',
        message: (data && data.message) || statusText,
        confirmButtonText: 'OK',
      })
    }
    if (status === 401) {
      showDialog({
        title: 'Error',
        message: 'Authorization verification failed',
        confirmButtonText: 'OK',
      }).then(() => {
        window.location.href = '/'
      })
    }
  }
  return Promise.reject(error)
}

// 请求拦截器
function requestHandler(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig> {
  const requestKey = createRequestKey(config)

  // 检查是否有相同的请求正在进行
  if (pendingRequests.has(requestKey)) {
    return pendingRequests.get(requestKey)!
  }

  // 创建新的请求Promise
  const requestPromise = new Promise<InternalAxiosRequestConfig>((resolve, reject) => {
    try {
      const userStore = useUserStore()
      const authorizationHeader = userStore.getAuthorizationHeader()

      if (authorizationHeader) {
        config.headers[REQUEST_TOKEN_KEY] = authorizationHeader
      }

      if (config.url && !config.url.startsWith('/app-api')) {
        config.url = `/app-api${config.url}`
      }

      // 根据当前语言设置请求头
      const currentLanguage = localStorage.getItem('language') || 'en-US'

      // 语言代码映射 - 同时支持多种可能的键名
      const langMapping: Record<string, string[]> = {
        'pt-BR': ['pt', 'pt-BR', 'pt-BR'],
        'en-US': ['en', 'en-US'],
        'hi-IN': ['id', 'hi-IN', 'hi'], // 印地语支持多种可能的键名
        'tl-PH': ['tl', 'tl-PH'], // 菲律宾语支持
        'en': ['en', 'en-US'],
        'id': ['id', 'hi-IN', 'hi'],
        'tl': ['tl', 'tl-PH'] // 菲律宾语简化代码
      }

      const mappedLangs = langMapping[currentLanguage] || [currentLanguage]
      // 使用第一个映射的语言代码作为API请求的语言参数
      const apiLang = mappedLangs[0]
      config.headers.language = apiLang
      resolve(config)
    } catch (error) {
      reject(error)
    }
  })

  pendingRequests.set(requestKey, requestPromise)

  requestPromise.finally(() => {
    pendingRequests.delete(requestKey)
  })

  return requestPromise
}

request.interceptors.request.use(requestHandler, errorHandler)

// 响应拦截器
function responseHandler(response: { data: any }) {
  const res = response.data
  // 检查code是否为401，如果是则清除本地存储并跳转首页
  if (res.code === 401) {
    // 清除用户信息
    const userStore = useUserStore()
    userStore.clearUserInfo()

    // 只清除路由缓存，不影响其他用户信息
    const routeCacheStore = useRouteCacheStore()
    routeCacheStore.clearRouteCache()

    // 清除sessionStorage中的TabBar相关标记
    sessionStorage.removeItem('fromPerson')
    sessionStorage.removeItem('fromHome')

    // 只清除指定的key
    const keysToRemove = [
      'access_token',
      'refresh_token',
      'token',
      'user-store',
      'user_id',
      'rememberedCredentials',
      'topicsLastReferenceTime',
      'lastExternalReferrer',
      'lastExternalReferrerTime',
      'language',
      'vueuse-color-scheme'
    ]
    keysToRemove.forEach(key => {
      localStorage.removeItem(key)
      sessionStorage.removeItem(key)
    })

    // 跳转首页
    // window.location.href = '/'
    router.replace('/')
    return Promise.reject(res)
  }
  if (res.code == 503) {
    console.log('检测到503状态码')

    // 检查是否已经在404页面，避免重复跳转
    const currentPath = window.location.pathname
    if (currentPath === '/404') {
      console.log('已在404页面，跳过跳转')
      return Promise.reject(res)
    }

    // 检查是否已经跳转过，避免重复跳转
    const hasJumped = sessionStorage.getItem('maintenanceJumped')
    if (hasJumped) {
      console.log('已经跳转过，跳过跳转')
      return Promise.reject(res)
    }

    console.log('准备跳转到404页面')

    // 设置跳转标记
    sessionStorage.setItem('maintenanceJumped', 'true')

    // 保存维护信息到localStorage
    if (res.data) {
      localStorage.setItem('maintenanceInfo', JSON.stringify(res.data))
    }

    // 直接使用window.location.href跳转
    window.location.href = '/404'

    return Promise.reject(res)
  }


  // 其他错误码处理
  if (res.code !== 0 && res.code !== 200) {
    (window as any).$toast(res.msg || 'Request failed')
    return Promise.reject(res)
  }

  return res
}

request.interceptors.response.use(responseHandler, errorHandler)

// 轮播图专用缓存请求方法
async function getBannerCached(): Promise<any> {
  const cacheKey = 'GET_/app-api/platform/banner/getAll_null'

  // 检查缓存
  const cached = CacheManager.get(cacheKey)
  if (cached) {
    return cached
  }

  // 执行请求
  const data = await request.get('/platform/banner/getAll')

  // 缓存结果
  CacheManager.set(cacheKey, data, BANNER_CACHE_TTL)

  return data
}

// 跑马灯专用缓存请求方法
async function getMarqueeCached(): Promise<any> {
  const cacheKey = 'GET_/app-api/center/marquee/getValid_null'

  // 检查缓存
  const cached = CacheManager.get(cacheKey)
  if (cached) {
    return cached
  }

  // 执行请求
  const data = await request.get('/center/marquee/getValid')

  // 缓存结果
  CacheManager.set(cacheKey, data, MARQUEE_CACHE_TTL)

  return data
}

// 厂商列表专用缓存请求方法 - 修复版本
async function getVendorsCached(params?: { gameType?: string | null }): Promise<any> {
  const gameType = params?.gameType || null
  const cacheKey = `GET_/app-api/platform/game-vendor/getVendors_${gameType}`

  // 检查缓存
  const cached = CacheManager.get(cacheKey)
  if (cached) {
    console.log('[缓存命中] 厂商列表数据')
    return cached
  }

  // 执行请求
  const data = await request.get('/platform/game-vendor/getVendors', { params: { gameType:"" } })

  // 缓存结果
  CacheManager.set(cacheKey, data, VENDORS_CACHE_TTL)
  console.log('[缓存设置] 厂商列表数据, TTL: 15分钟')

  return data
}

// 导出 http 对象
export const http = {
  // 基础请求方法
  get: (url: string, params?: any) => request.get(url, { params }),
  post: (url: string, data?: any) => request.post(url, data),
  put: (url: string, data?: any) => request.put(url, data),
  delete: (url: string, params?: any) => request.delete(url, { params }),
  patch: (url: string, data?: any) => request.patch(url, data),

  // 轮播图专用缓存方法
  getBannerCached,

  // 跑马灯专用缓存方法
  getMarqueeCached,

  // 厂商列表专用缓存方法
  getVendorsCached,

  // 清除轮播图缓存
  clearBannerCache: () => {
    const cacheKey = 'GET_/app-api/platform/banner/getAll_null'
    requestCache.delete(cacheKey)
    console.log('[缓存清理] 轮播图缓存已清除')
  },

  // 清除厂商列表缓存
  clearVendorsCache: () => {
    const cacheKey = 'GET_/app-api/platform/game-vendor/getVendors_null'
    requestCache.delete(cacheKey)
    console.log('[缓存清理] 厂商列表缓存已清除')
  },

  // 缓存管理方法
  cache: {
    get: CacheManager.get,
    set: CacheManager.set,
    clear: CacheManager.clear,
    clearExpired: CacheManager.clearExpired,
  }
}

// 定期清理过期缓存（每5分钟执行一次）
setInterval(() => {
  CacheManager.clearExpired()
}, 5 * 60 * 1000)

export { CacheManager }
export default request
